import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaFire, FaGift, FaShieldAlt, FaClock, FaTrophy, FaArrowRight, FaChevronDown, FaBalanceScale, FaUsers, FaGamepad, FaCommentDots, FaChartLine, FaCheckCircle, FaAward, FaNewspaper, FaPlay, FaInfoCircle } from 'react-icons/fa';
import SEO from '../components/SEO';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import NewsModal from '../components/NewsModal';
import GuideModal from '../components/GuideModal';

const Home: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const offersRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const bonusesRef = useRef<HTMLDivElement>(null);
  const securityRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const [selectedNews, setSelectedNews] = useState<number | null>(null);
  const [selectedGuide, setSelectedGuide] = useState<number | null>(null);

  const newsItems = [
    { 
      title: 'New Casino Launches in UK', 
      date: 'Dec 5, 2025', 
      desc: 'Latest UKGC licensed casino with exclusive welcome bonus', 
      category: 'News',
      fullContent: `The UK online casino market continues to expand with the launch of several new UKGC-licensed operators this month. These new casinos bring fresh gaming experiences, innovative features, and competitive welcome bonuses to UK players.

One standout newcomer offers a generous 200% welcome bonus up to £200, with wagering requirements set at a reasonable 30x. The casino features over 2,000 games from top providers including NetEnt, Microgaming, and Evolution Gaming.

What makes this launch particularly noteworthy is the casino's commitment to responsible gambling. They've implemented advanced player protection tools, including deposit limits, reality checks, and easy access to self-exclusion options.

Our team has thoroughly tested the platform and can confirm fast withdrawal times, excellent customer support, and a user-friendly mobile experience. The casino accepts all major UK payment methods including Visa, Mastercard, PayPal, and bank transfers.

For players looking to try something new, this casino represents an excellent opportunity to explore a fresh platform while benefiting from a substantial welcome offer. As always, we recommend reading the full terms and conditions before claiming any bonus.`
    },
    { 
      title: 'Best Bonuses This Month', 
      date: 'Dec 3, 2025', 
      desc: 'Top 5 casino bonuses with lowest wagering requirements', 
      category: 'Bonuses',
      fullContent: `December brings some of the best casino bonuses we've seen this year. After extensive research, we've identified the top 5 offers that provide genuine value to UK players.

Leading the pack is a 150% welcome bonus with just 25x wagering requirements - one of the lowest we've encountered. This offer is particularly attractive because it applies to both the deposit and bonus amount, making it easier to meet the playthrough requirements.

Second place goes to a cashback offer that returns 20% of losses up to £100 weekly. This type of bonus is excellent for regular players as it provides ongoing value rather than a one-time welcome offer.

The third spot features a no-deposit bonus of 50 free spins with no wagering requirements on winnings. While the maximum win is capped, this is a fantastic way to try a casino risk-free.

Our fourth recommendation is a reload bonus that offers 75% extra on deposits made every Friday. Combined with the welcome bonus, this creates excellent value for new players.

Rounding out the top 5 is a VIP program that provides exclusive bonuses, faster withdrawals, and dedicated account managers. While this requires building up play history, the long-term benefits are substantial.

Remember, always check the terms and conditions. Lower wagering requirements and fair bonus terms are what separate great offers from marketing gimmicks.`
    },
    { 
      title: 'Payment Method Updates', 
      date: 'Dec 1, 2025', 
      desc: 'New fast withdrawal options available at top casinos', 
      category: 'Updates',
      fullContent: `The UK casino payment landscape has seen significant improvements this month, with several major operators introducing faster withdrawal methods and lower fees.

PayPal withdrawals, already popular for their speed, are now available at three additional top-rated casinos. These withdrawals typically process within 2-4 hours, making PayPal one of the fastest options available.

Apple Pay and Google Pay have also expanded their presence in the UK casino market. These mobile payment methods offer instant deposits and are increasingly being accepted for withdrawals, though processing times vary by operator.

Cryptocurrency options continue to grow, with Bitcoin and Ethereum now accepted at over 15 UK-licensed casinos. Crypto withdrawals are often instant or process within minutes, though players should be aware of potential volatility.

Bank transfers have seen improvements too, with some casinos now offering same-day processing for transfers over £500. This is a significant improvement from the previous 3-5 business day standard.

E-wallet options like Skrill and Neteller remain popular for their balance of speed and security. Most casinos process these withdrawals within 24 hours, though some premium operators offer instant processing.

For players prioritizing speed, we recommend using e-wallets or cryptocurrency. For those who prefer traditional methods, bank transfers are becoming more efficient, though still slower than digital alternatives.

Always check each casino's payment page for the most up-to-date information on available methods, processing times, and any associated fees.`
    },
  ];

  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const topOffers = [
    {
      id: 'allbritishcasino',
      name: 'All British Casino',
      image: `${process.env.PUBLIC_URL}/images/offers/allbritishcasino.webp`,
      url: 'https://www.allbritishcasino.com/',
      rating: 4.8,
      bonus: '100% up to £100',
      features: ['Fast Withdrawals', 'UK Licensed', 'Live Casino'],
      color: 'from-blue-500 to-blue-700'
    },
    {
      id: 'casinocasino',
      name: 'Casino Casino',
      image: `${process.env.PUBLIC_URL}/images/offers/casinocasino.webp`,
      url: 'https://www.casinocasino.com',
      rating: 4.7,
      bonus: '200% up to £200',
      features: ['VIP Program', 'Mobile App', '24/7 Support'],
      color: 'from-purple-500 to-purple-700'
    },
    {
      id: 'pubcasino',
      name: 'Pub Casino',
      image: `${process.env.PUBLIC_URL}/images/offers/pubcasino.webp`,
      url: 'https://www.pubcasino.co.uk/',
      rating: 4.6,
      bonus: '150% up to £150',
      features: ['Sports Betting', 'Cashback', 'Tournaments'],
      color: 'from-green-500 to-green-700'
    },
  ];

  const performanceData = [
    { month: 'Jan', withdrawals: 95, deposits: 100 },
    { month: 'Feb', withdrawals: 98, deposits: 105 },
    { month: 'Mar', withdrawals: 97, deposits: 110 },
    { month: 'Apr', withdrawals: 99, deposits: 115 },
    { month: 'May', withdrawals: 100, deposits: 120 },
    { month: 'Jun', withdrawals: 98, deposits: 125 },
  ];

  const bonusComparison = [
    { casino: 'All British', welcome: 100, reload: 50, cashback: 10 },
    { casino: 'Casino Casino', welcome: 200, reload: 75, cashback: 15 },
    { casino: 'Pub Casino', welcome: 150, reload: 60, cashback: 12 },
  ];

  const heroAnchors = [
    { label: 'Top Offers', ref: offersRef, icon: FaFire },
    { label: 'Reviews', ref: reviewsRef, icon: FaStar },
    { label: 'Bonuses', ref: bonusesRef, icon: FaGift },
    { label: 'Security', ref: securityRef, icon: FaShieldAlt },
    { label: 'Guides', ref: guidesRef, icon: FaTrophy },
    { label: 'Compare', ref: offersRef, icon: FaBalanceScale },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'JackpotQuantum.com',
    url: 'https://jackpotquantum.com',
    description: 'Comprehensive UK casino reviews, exclusive bonuses, and expert guides. Find the best casino offers trusted by thousands of players.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://jackpotquantum.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <>
      <SEO
        title="UK Casino Reviews & Exclusive Bonuses"
        description="Discover the best UK casino offers with comprehensive reviews, exclusive bonuses, and expert insights. Trusted by thousands of players."
        jsonLd={jsonLd}
        ogImage="/images/logo.webp"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/heromainbg.webp)` }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-transparent to-transparent"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-20 text-center">
          <div className="mb-8 animate-float">
            <img 
              src={`${process.env.PUBLIC_URL}/images/logo.webp`}
              alt="JackpotQuantum"
              className="h-32 md:h-48 w-auto mx-auto mb-8"
              loading="eager"
            />
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8">
              Your ultimate destination for UK casino reviews, exclusive bonuses, and winning strategies
            </p>
          </div>

          {/* Hero Anchor Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {heroAnchors.map((anchor) => {
              const Icon = anchor.icon;
              return (
                <button
                  key={anchor.label}
                  onClick={() => scrollToSection(anchor.ref)}
                  className="group glass px-6 py-3 rounded-xl flex items-center space-x-2 hover:bg-quantum-500/20 transition-all transform hover:scale-105"
                >
                  <Icon className="w-5 h-5 text-quantum-400 group-hover:text-quantum-300" />
                  <span className="text-white font-medium">{anchor.label}</span>
                </button>
              );
            })}
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => scrollToSection(offersRef)}
              className="animate-bounce text-quantum-400 hover:text-quantum-300 transition-colors"
              aria-label="Scroll down"
            >
              <FaChevronDown className="w-8 h-8" />
            </button>
          </div>
        </div>
      </section>

      {/* Top Offers Section */}
      <section ref={offersRef} className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Top Casino Offers</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Handpicked UK casinos with the best bonuses, fastest payouts, and exceptional player experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {topOffers.map((offer, index) => (
              <div
                key={offer.id}
                className="glass rounded-2xl overflow-hidden card-hover group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="h-48 relative overflow-hidden bg-dark-800">
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="w-full h-full object-contain relative z-10"
                    onError={(e) => {
                      console.error('Image failed to load:', offer.image);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${offer.color} opacity-30 pointer-events-none z-1`}></div>
                  <div className="absolute top-4 right-4 flex items-center space-x-1 bg-dark-900/80 px-3 py-1 rounded-full z-10">
                    <FaStar className="w-4 h-4 text-yellow-400" />
                    <span className="text-white font-semibold">{offer.rating}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{offer.name}</h3>
                  <div className="flex items-center space-x-2 mb-4">
                    <FaGift className="w-5 h-5 text-quantum-400" />
                    <span className="text-quantum-400 font-semibold">{offer.bonus}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {offer.features.map((feature, i) => (
                      <li key={i} className="flex items-center space-x-2 text-gray-300">
                        <div className="w-2 h-2 bg-quantum-400 rounded-full"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-gradient-to-r from-quantum-500 to-quantum-600 text-white py-3 px-6 rounded-lg font-semibold text-center hover:from-quantum-600 hover:to-quantum-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2"
                    >
                      <span>Visit Casino</span>
                      <FaArrowRight className="w-4 h-4" />
                    </a>
                    <Link
                      to={`/review/${offer.id}`}
                      className="flex-1 glass text-white py-3 px-6 rounded-lg font-semibold text-center hover:bg-dark-700 transition-all flex items-center justify-center space-x-2"
                    >
                      <span>Read Review</span>
                      <FaArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section ref={reviewsRef} className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Expert Reviews</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              In-depth analysis of UK casinos covering games, bonuses, security, and player experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="glass rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
                <FaStar className="w-6 h-6 text-yellow-400" />
                <span>What We Review</span>
              </h3>
              <ul className="space-y-3">
                {['Game Selection & Quality', 'Bonus Terms & Wagering', 'Withdrawal Speed', 'Customer Support', 'Mobile Experience', 'Security & Licensing'].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3 text-gray-300">
                    <div className="w-6 h-6 bg-quantum-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 bg-quantum-400 rounded-full"></div>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="glass rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center space-x-2">
                <FaTrophy className="w-6 h-6 text-quantum-400" />
                <span>Our Rating System</span>
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Overall Rating', value: 95, color: 'bg-quantum-500' },
                  { label: 'Trust Score', value: 98, color: 'bg-green-500' },
                  { label: 'Player Satisfaction', value: 92, color: 'bg-yellow-500' },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300">{item.label}</span>
                      <span className="text-white font-semibold">{item.value}%</span>
                    </div>
                    <div className="w-full bg-dark-700 rounded-full h-3">
                      <div
                        className={`${item.color} h-3 rounded-full transition-all duration-1000`}
                        style={{ width: `${item.value}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section ref={bonusesRef} className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Bonus Comparison</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Compare welcome bonuses, reload offers, and cashback programs across top UK casinos
            </p>
          </div>

          <div className="glass rounded-2xl p-8 mb-8">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={bonusComparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="casino" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Bar dataKey="welcome" fill="#0ea5e9" name="Welcome Bonus" />
                <Bar dataKey="reload" fill="#3b82f6" name="Reload Bonus" />
                <Bar dataKey="cashback" fill="#60a5fa" name="Cashback %" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: FaGift, title: 'Welcome Bonuses', desc: 'Up to £200 matched deposits for new players' },
              { icon: FaFire, title: 'Reload Offers', desc: 'Weekly bonuses to boost your balance' },
              { icon: FaTrophy, title: 'VIP Rewards', desc: 'Exclusive perks for loyal players' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="glass rounded-xl p-6 text-center card-hover">
                  <div className="w-16 h-16 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-dark-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section ref={securityRef} className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Security & Trust</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              We only recommend casinos licensed by the UK Gambling Commission with proven security measures
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FaShieldAlt, title: 'UKGC Licensed', desc: 'All casinos are fully licensed and regulated' },
              { icon: FaClock, title: 'Fast Payouts', desc: 'Withdrawals processed within 24 hours' },
              { icon: FaShieldAlt, title: 'SSL Encryption', desc: 'Bank-level security for all transactions' },
              { icon: FaTrophy, title: 'Fair Play', desc: 'Regular audits ensure game fairness' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="glass rounded-xl p-6 text-center card-hover">
                  <div className="w-16 h-16 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-dark-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Guides Section */}
      <section ref={guidesRef} className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Expert Guides</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Learn winning strategies, understand bonus terms, and maximize your casino experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Understanding Wagering Requirements',
                desc: 'Learn how to clear bonus terms and withdraw your winnings faster',
                icon: FaTrophy,
                fullContent: `Wagering requirements, also known as playthrough requirements, are one of the most important factors to consider when claiming a casino bonus. These requirements determine how many times you must bet your bonus amount before you can withdraw any winnings.

Understanding the Basics

When you claim a welcome bonus, the casino typically requires you to wager the bonus amount (and sometimes the deposit amount) a certain number of times before you can cash out. For example, if you receive a £100 bonus with 35x wagering requirements, you must place bets totaling £3,500 before you can withdraw.

Why Wagering Requirements Exist

Casinos implement wagering requirements to prevent bonus abuse and ensure players actually use the bonus for gaming rather than immediately withdrawing it. This is a standard practice across the industry and is required by UK gambling regulations.

How to Calculate Real Value

To understand the true value of a bonus, calculate the total wagering requirement. A £100 bonus with 35x wagering means £3,500 in bets. Compare this across different casinos to find the best value. Lower wagering requirements (25x-30x) are generally better than higher ones (40x-50x).

Game Contributions

Not all games contribute equally to wagering requirements. Slots typically contribute 100%, while table games like blackjack and roulette often contribute only 10-20%. Live dealer games may contribute even less. Always check the casino's terms to see which games count toward wagering.

Tips for Clearing Requirements

1. Focus on slots if you want to clear requirements faster
2. Avoid high-variance games that can deplete your balance quickly
3. Set a budget and stick to it
4. Take advantage of games with higher RTP (Return to Player) percentages
5. Don't chase losses trying to meet wagering requirements

Time Limits

Most bonuses have time limits, typically 30 days. Make sure you can realistically meet the wagering requirements within this timeframe. If not, the bonus and any winnings may be forfeited.

Reading the Fine Print

Always read the full terms and conditions before claiming any bonus. Look for:
- Wagering requirements (bonus only vs. bonus + deposit)
- Game contribution percentages
- Maximum bet limits while using bonus funds
- Time limits
- Restricted games or payment methods

By understanding wagering requirements, you can make informed decisions about which bonuses offer genuine value and which are simply marketing gimmicks.`
              },
              {
                title: 'Best Payment Methods for UK Players',
                desc: 'Compare withdrawal speeds, fees, and security of different payment options',
                icon: FaClock,
                fullContent: `Choosing the right payment method is crucial for a smooth casino experience. Different methods offer varying speeds, security levels, and convenience. Here's a comprehensive guide to the best payment options for UK casino players.

E-Wallets: Speed and Convenience

PayPal, Skrill, and Neteller are among the fastest payment methods available. Deposits are typically instant, and withdrawals often process within 24 hours, sometimes even faster. These services act as intermediaries between your bank and the casino, adding an extra layer of security.

Advantages:
- Fast deposits and withdrawals
- Enhanced security and privacy
- Widely accepted at UK casinos
- Easy to manage multiple casino accounts

Considerations:
- Some casinos may charge fees for e-wallet withdrawals
- Not all casinos accept all e-wallet providers
- Account verification may be required

Debit Cards: Traditional and Reliable

Visa and Mastercard debit cards are the most widely accepted payment methods. They're familiar, secure, and convenient for most players.

Advantages:
- Universal acceptance
- Familiar and easy to use
- Strong consumer protection
- Direct bank integration

Considerations:
- Withdrawals can take 3-5 business days
- Some banks may block gambling transactions
- Less privacy than e-wallets

Bank Transfers: For Larger Amounts

Direct bank transfers are ideal for larger deposits and withdrawals. While slower, they're highly secure and often have higher limits.

Advantages:
- High transaction limits
- Very secure
- No intermediary fees
- Suitable for large amounts

Considerations:
- Slower processing (3-5 business days)
- May require additional verification
- Less convenient for smaller transactions

Cryptocurrency: The Future of Payments

Bitcoin and other cryptocurrencies are becoming increasingly popular at UK casinos. They offer anonymity and often instant processing.

Advantages:
- Fast transactions
- Enhanced privacy
- Lower fees
- No chargebacks

Considerations:
- Price volatility
- Not all casinos accept crypto
- Requires understanding of cryptocurrency
- Regulatory considerations

Prepaid Cards: Budget Control

Prepaid cards like Paysafecard allow you to deposit without linking a bank account or card, providing excellent budget control.

Advantages:
- No bank account needed
- Excellent for budget management
- Widely available
- Enhanced privacy

Considerations:
- Cannot be used for withdrawals
- May have lower limits
- Need to purchase separately

Choosing the Right Method

Consider your priorities:
- Speed: Choose e-wallets or cryptocurrency
- Security: Bank transfers or debit cards
- Privacy: E-wallets or cryptocurrency
- Convenience: Debit cards or e-wallets
- Budget control: Prepaid cards

Always check each casino's payment page for specific information about processing times, fees, and limits. Some casinos offer faster processing for VIP members or specific payment methods.`
              },
              {
                title: 'Maximizing Welcome Bonuses',
                desc: 'Strategies to get the most value from casino sign-up offers',
                icon: FaGift,
                fullContent: `Welcome bonuses can significantly boost your initial bankroll, but maximizing their value requires strategy and understanding. Here's how to get the most from casino sign-up offers.

Understanding Bonus Types

Match Bonuses: The casino matches your deposit up to a certain amount. For example, a 100% match up to £100 means depositing £100 gives you £200 total.

Free Spins: A set number of spins on selected slot games, often with winnings subject to wagering requirements.

No Deposit Bonuses: Free bonus funds or spins just for signing up, requiring no deposit.

Package Bonuses: Multiple bonuses across your first few deposits, offering more total value but requiring multiple deposits.

Evaluating Bonus Value

Don't just look at the bonus amount. Consider:
- Wagering requirements (lower is better)
- Maximum bet limits while using bonus
- Game restrictions and contribution percentages
- Time limits to meet requirements
- Maximum withdrawal limits from bonus winnings

The Real Value Calculation

A £200 bonus with 40x wagering = £8,000 in required bets
A £100 bonus with 25x wagering = £2,500 in required bets

The second offer is actually better value despite being half the amount!

Strategy for Match Bonuses

1. Deposit the maximum amount to get the full bonus
2. If you can't afford the max, deposit an amount that gives you good value
3. Check if the bonus applies to your first deposit only or multiple deposits
4. Understand if wagering applies to bonus only or bonus + deposit

Free Spins Strategy

1. Check which games the spins are valid on
2. Look for free spins with no wagering requirements (rare but valuable)
3. Understand the maximum win limits
4. Use spins on games with higher RTP when possible
5. Don't feel rushed - use spins within the time limit

No Deposit Bonuses

These are excellent for trying a casino risk-free, but:
- Usually have very high wagering requirements
- Often have low maximum withdrawal limits
- May require a deposit before you can withdraw
- Check if you need to make a deposit to activate

Package Bonuses

Multi-deposit packages can offer excellent value:
- First deposit: 100% up to £100
- Second deposit: 50% up to £100
- Third deposit: 25% up to £100

Total potential bonus: £175 across three deposits

Timing Your Deposits

1. Wait for promotional periods when bonuses are enhanced
2. Check if the casino offers reload bonuses regularly
3. Consider if you'll have time to meet wagering requirements
4. Don't claim bonuses you can't realistically use

Game Selection

When using bonus funds:
- Focus on games that contribute 100% to wagering (usually slots)
- Avoid games with low contribution percentages
- Consider RTP - higher is generally better
- Avoid high-variance games that can deplete balance quickly

Bankroll Management

1. Never deposit more than you can afford to lose
2. Set a budget for meeting wagering requirements
3. Don't chase losses trying to clear bonus terms
4. Consider the bonus as part of your entertainment budget

Reading Terms Carefully

Always read the full terms before claiming:
- Wagering requirements and what they apply to
- Game restrictions and contribution percentages
- Maximum bet limits
- Time limits
- Withdrawal restrictions
- Country restrictions

Common Pitfalls to Avoid

1. Claiming bonuses without reading terms
2. Trying to clear requirements too quickly
3. Using bonus on restricted games
4. Exceeding maximum bet limits
5. Not meeting time requirements
6. Expecting to withdraw immediately

Maximizing Long-Term Value

Look beyond the welcome bonus:
- Check for ongoing promotions and reload bonuses
- Consider VIP programs and loyalty rewards
- Look for cashback offers
- Check for regular tournaments and competitions

Remember: The best bonus is one you can actually use and enjoy. Don't be swayed by large numbers if the terms make it impossible to benefit. Focus on bonuses with fair terms that match your playing style and budget.`
              },
            ].map((guide, i) => {
              const Icon = guide.icon;
              return (
                <div 
                  key={i} 
                  className="glass rounded-xl p-6 card-hover cursor-pointer"
                  onClick={() => setSelectedGuide(i)}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-dark-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{guide.title}</h3>
                  <p className="text-gray-400 mb-4">{guide.desc}</p>
                  <button 
                    className="text-quantum-400 hover:text-quantum-300 font-semibold flex items-center space-x-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedGuide(i);
                    }}
                  >
                    <span>Read More</span>
                    <FaArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {selectedGuide !== null && (
            <GuideModal
              isOpen={selectedGuide !== null}
              onClose={() => setSelectedGuide(null)}
              title={[
                {
                  title: 'Understanding Wagering Requirements',
                  desc: 'Learn how to clear bonus terms and withdraw your winnings faster',
                  icon: FaTrophy,
                  fullContent: `Wagering requirements, also known as playthrough requirements, are one of the most important factors to consider when claiming a casino bonus. These requirements determine how many times you must bet your bonus amount before you can withdraw any winnings.

Understanding the Basics

When you claim a welcome bonus, the casino typically requires you to wager the bonus amount (and sometimes the deposit amount) a certain number of times before you can cash out. For example, if you receive a £100 bonus with 35x wagering requirements, you must place bets totaling £3,500 before you can withdraw.

Why Wagering Requirements Exist

Casinos implement wagering requirements to prevent bonus abuse and ensure players actually use the bonus for gaming rather than immediately withdrawing it. This is a standard practice across the industry and is required by UK gambling regulations.

How to Calculate Real Value

To understand the true value of a bonus, calculate the total wagering requirement. A £100 bonus with 35x wagering means £3,500 in bets. Compare this across different casinos to find the best value. Lower wagering requirements (25x-30x) are generally better than higher ones (40x-50x).

Game Contributions

Not all games contribute equally to wagering requirements. Slots typically contribute 100%, while table games like blackjack and roulette often contribute only 10-20%. Live dealer games may contribute even less. Always check the casino's terms to see which games count toward wagering.

Tips for Clearing Requirements

1. Focus on slots if you want to clear requirements faster
2. Avoid high-variance games that can deplete your balance quickly
3. Set a budget and stick to it
4. Take advantage of games with higher RTP (Return to Player) percentages
5. Don't chase losses trying to meet wagering requirements

Time Limits

Most bonuses have time limits, typically 30 days. Make sure you can realistically meet the wagering requirements within this timeframe. If not, the bonus and any winnings may be forfeited.

Reading the Fine Print

Always read the full terms and conditions before claiming any bonus. Look for:
- Wagering requirements (bonus only vs. bonus + deposit)
- Game contribution percentages
- Maximum bet limits while using bonus funds
- Time limits
- Restricted games or payment methods

By understanding wagering requirements, you can make informed decisions about which bonuses offer genuine value and which are simply marketing gimmicks.`
                },
                {
                  title: 'Best Payment Methods for UK Players',
                  desc: 'Compare withdrawal speeds, fees, and security of different payment options',
                  icon: FaClock,
                  fullContent: `Choosing the right payment method is crucial for a smooth casino experience. Different methods offer varying speeds, security levels, and convenience. Here's a comprehensive guide to the best payment options for UK casino players.

E-Wallets: Speed and Convenience

PayPal, Skrill, and Neteller are among the fastest payment methods available. Deposits are typically instant, and withdrawals often process within 24 hours, sometimes even faster. These services act as intermediaries between your bank and the casino, adding an extra layer of security.

Advantages:
- Fast deposits and withdrawals
- Enhanced security and privacy
- Widely accepted at UK casinos
- Easy to manage multiple casino accounts

Considerations:
- Some casinos may charge fees for e-wallet withdrawals
- Not all casinos accept all e-wallet providers
- Account verification may be required

Debit Cards: Traditional and Reliable

Visa and Mastercard debit cards are the most widely accepted payment methods. They're familiar, secure, and convenient for most players.

Advantages:
- Universal acceptance
- Familiar and easy to use
- Strong consumer protection
- Direct bank integration

Considerations:
- Withdrawals can take 3-5 business days
- Some banks may block gambling transactions
- Less privacy than e-wallets

Bank Transfers: For Larger Amounts

Direct bank transfers are ideal for larger deposits and withdrawals. While slower, they're highly secure and often have higher limits.

Advantages:
- High transaction limits
- Very secure
- No intermediary fees
- Suitable for large amounts

Considerations:
- Slower processing (3-5 business days)
- May require additional verification
- Less convenient for smaller transactions

Cryptocurrency: The Future of Payments

Bitcoin and other cryptocurrencies are becoming increasingly popular at UK casinos. They offer anonymity and often instant processing.

Advantages:
- Fast transactions
- Enhanced privacy
- Lower fees
- No chargebacks

Considerations:
- Price volatility
- Not all casinos accept crypto
- Requires understanding of cryptocurrency
- Regulatory considerations

Prepaid Cards: Budget Control

Prepaid cards like Paysafecard allow you to deposit without linking a bank account or card, providing excellent budget control.

Advantages:
- No bank account needed
- Excellent for budget management
- Widely available
- Enhanced privacy

Considerations:
- Cannot be used for withdrawals
- May have lower limits
- Need to purchase separately

Choosing the Right Method

Consider your priorities:
- Speed: Choose e-wallets or cryptocurrency
- Security: Bank transfers or debit cards
- Privacy: E-wallets or cryptocurrency
- Convenience: Debit cards or e-wallets
- Budget control: Prepaid cards

Always check each casino's payment page for specific information about processing times, fees, and limits. Some casinos offer faster processing for VIP members or specific payment methods.`
                },
                {
                  title: 'Maximizing Welcome Bonuses',
                  desc: 'Strategies to get the most value from casino sign-up offers',
                  icon: FaGift,
                  fullContent: `Welcome bonuses can significantly boost your initial bankroll, but maximizing their value requires strategy and understanding. Here's how to get the most from casino sign-up offers.

Understanding Bonus Types

Match Bonuses: The casino matches your deposit up to a certain amount. For example, a 100% match up to £100 means depositing £100 gives you £200 total.

Free Spins: A set number of spins on selected slot games, often with winnings subject to wagering requirements.

No Deposit Bonuses: Free bonus funds or spins just for signing up, requiring no deposit.

Package Bonuses: Multiple bonuses across your first few deposits, offering more total value but requiring multiple deposits.

Evaluating Bonus Value

Don't just look at the bonus amount. Consider:
- Wagering requirements (lower is better)
- Maximum bet limits while using bonus
- Game restrictions and contribution percentages
- Time limits to meet requirements
- Maximum withdrawal limits from bonus winnings

The Real Value Calculation

A £200 bonus with 40x wagering = £8,000 in required bets
A £100 bonus with 25x wagering = £2,500 in required bets

The second offer is actually better value despite being half the amount!

Strategy for Match Bonuses

1. Deposit the maximum amount to get the full bonus
2. If you can't afford the max, deposit an amount that gives you good value
3. Check if the bonus applies to your first deposit only or multiple deposits
4. Understand if wagering applies to bonus only or bonus + deposit

Free Spins Strategy

1. Check which games the spins are valid on
2. Look for free spins with no wagering requirements (rare but valuable)
3. Understand the maximum win limits
4. Use spins on games with higher RTP when possible
5. Don't feel rushed - use spins within the time limit

No Deposit Bonuses

These are excellent for trying a casino risk-free, but:
- Usually have very high wagering requirements
- Often have low maximum withdrawal limits
- May require a deposit before you can withdraw
- Check if you need to make a deposit to activate

Package Bonuses

Multi-deposit packages can offer excellent value:
- First deposit: 100% up to £100
- Second deposit: 50% up to £100
- Third deposit: 25% up to £100

Total potential bonus: £175 across three deposits

Timing Your Deposits

1. Wait for promotional periods when bonuses are enhanced
2. Check if the casino offers reload bonuses regularly
3. Consider if you'll have time to meet wagering requirements
4. Don't claim bonuses you can't realistically use

Game Selection

When using bonus funds:
- Focus on games that contribute 100% to wagering (usually slots)
- Avoid games with low contribution percentages
- Consider RTP - higher is generally better
- Avoid high-variance games that can deplete balance quickly

Bankroll Management

1. Never deposit more than you can afford to lose
2. Set a budget for meeting wagering requirements
3. Don't chase losses trying to clear bonus terms
4. Consider the bonus as part of your entertainment budget

Reading Terms Carefully

Always read the full terms before claiming:
- Wagering requirements and what they apply to
- Game restrictions and contribution percentages
- Maximum bet limits
- Time limits
- Withdrawal restrictions
- Country restrictions

Common Pitfalls to Avoid

1. Claiming bonuses without reading terms
2. Trying to clear requirements too quickly
3. Using bonus on restricted games
4. Exceeding maximum bet limits
5. Not meeting time requirements
6. Expecting to withdraw immediately

Maximizing Long-Term Value

Look beyond the welcome bonus:
- Check for ongoing promotions and reload bonuses
- Consider VIP programs and loyalty rewards
- Look for cashback offers
- Check for regular tournaments and competitions

Remember: The best bonus is one you can actually use and enjoy. Don't be swayed by large numbers if the terms make it impossible to benefit. Focus on bonuses with fair terms that match your playing style and budget.`
                },
              ][selectedGuide].title}
              description={[
                {
                  title: 'Understanding Wagering Requirements',
                  desc: 'Learn how to clear bonus terms and withdraw your winnings faster',
                  icon: FaTrophy,
                  fullContent: `Wagering requirements, also known as playthrough requirements, are one of the most important factors to consider when claiming a casino bonus. These requirements determine how many times you must bet your bonus amount before you can withdraw any winnings.

Understanding the Basics

When you claim a welcome bonus, the casino typically requires you to wager the bonus amount (and sometimes the deposit amount) a certain number of times before you can cash out. For example, if you receive a £100 bonus with 35x wagering requirements, you must place bets totaling £3,500 before you can withdraw.

Why Wagering Requirements Exist

Casinos implement wagering requirements to prevent bonus abuse and ensure players actually use the bonus for gaming rather than immediately withdrawing it. This is a standard practice across the industry and is required by UK gambling regulations.

How to Calculate Real Value

To understand the true value of a bonus, calculate the total wagering requirement. A £100 bonus with 35x wagering means £3,500 in bets. Compare this across different casinos to find the best value. Lower wagering requirements (25x-30x) are generally better than higher ones (40x-50x).

Game Contributions

Not all games contribute equally to wagering requirements. Slots typically contribute 100%, while table games like blackjack and roulette often contribute only 10-20%. Live dealer games may contribute even less. Always check the casino's terms to see which games count toward wagering.

Tips for Clearing Requirements

1. Focus on slots if you want to clear requirements faster
2. Avoid high-variance games that can deplete your balance quickly
3. Set a budget and stick to it
4. Take advantage of games with higher RTP (Return to Player) percentages
5. Don't chase losses trying to meet wagering requirements

Time Limits

Most bonuses have time limits, typically 30 days. Make sure you can realistically meet the wagering requirements within this timeframe. If not, the bonus and any winnings may be forfeited.

Reading the Fine Print

Always read the full terms and conditions before claiming any bonus. Look for:
- Wagering requirements (bonus only vs. bonus + deposit)
- Game contribution percentages
- Maximum bet limits while using bonus funds
- Time limits
- Restricted games or payment methods

By understanding wagering requirements, you can make informed decisions about which bonuses offer genuine value and which are simply marketing gimmicks.`
                },
                {
                  title: 'Best Payment Methods for UK Players',
                  desc: 'Compare withdrawal speeds, fees, and security of different payment options',
                  icon: FaClock,
                  fullContent: `Choosing the right payment method is crucial for a smooth casino experience. Different methods offer varying speeds, security levels, and convenience. Here's a comprehensive guide to the best payment options for UK casino players.

E-Wallets: Speed and Convenience

PayPal, Skrill, and Neteller are among the fastest payment methods available. Deposits are typically instant, and withdrawals often process within 24 hours, sometimes even faster. These services act as intermediaries between your bank and the casino, adding an extra layer of security.

Advantages:
- Fast deposits and withdrawals
- Enhanced security and privacy
- Widely accepted at UK casinos
- Easy to manage multiple casino accounts

Considerations:
- Some casinos may charge fees for e-wallet withdrawals
- Not all casinos accept all e-wallet providers
- Account verification may be required

Debit Cards: Traditional and Reliable

Visa and Mastercard debit cards are the most widely accepted payment methods. They're familiar, secure, and convenient for most players.

Advantages:
- Universal acceptance
- Familiar and easy to use
- Strong consumer protection
- Direct bank integration

Considerations:
- Withdrawals can take 3-5 business days
- Some banks may block gambling transactions
- Less privacy than e-wallets

Bank Transfers: For Larger Amounts

Direct bank transfers are ideal for larger deposits and withdrawals. While slower, they're highly secure and often have higher limits.

Advantages:
- High transaction limits
- Very secure
- No intermediary fees
- Suitable for large amounts

Considerations:
- Slower processing (3-5 business days)
- May require additional verification
- Less convenient for smaller transactions

Cryptocurrency: The Future of Payments

Bitcoin and other cryptocurrencies are becoming increasingly popular at UK casinos. They offer anonymity and often instant processing.

Advantages:
- Fast transactions
- Enhanced privacy
- Lower fees
- No chargebacks

Considerations:
- Price volatility
- Not all casinos accept crypto
- Requires understanding of cryptocurrency
- Regulatory considerations

Prepaid Cards: Budget Control

Prepaid cards like Paysafecard allow you to deposit without linking a bank account or card, providing excellent budget control.

Advantages:
- No bank account needed
- Excellent for budget management
- Widely available
- Enhanced privacy

Considerations:
- Cannot be used for withdrawals
- May have lower limits
- Need to purchase separately

Choosing the Right Method

Consider your priorities:
- Speed: Choose e-wallets or cryptocurrency
- Security: Bank transfers or debit cards
- Privacy: E-wallets or cryptocurrency
- Convenience: Debit cards or e-wallets
- Budget control: Prepaid cards

Always check each casino's payment page for specific information about processing times, fees, and limits. Some casinos offer faster processing for VIP members or specific payment methods.`
                },
                {
                  title: 'Maximizing Welcome Bonuses',
                  desc: 'Strategies to get the most value from casino sign-up offers',
                  icon: FaGift,
                  fullContent: `Welcome bonuses can significantly boost your initial bankroll, but maximizing their value requires strategy and understanding. Here's how to get the most from casino sign-up offers.

Understanding Bonus Types

Match Bonuses: The casino matches your deposit up to a certain amount. For example, a 100% match up to £100 means depositing £100 gives you £200 total.

Free Spins: A set number of spins on selected slot games, often with winnings subject to wagering requirements.

No Deposit Bonuses: Free bonus funds or spins just for signing up, requiring no deposit.

Package Bonuses: Multiple bonuses across your first few deposits, offering more total value but requiring multiple deposits.

Evaluating Bonus Value

Don't just look at the bonus amount. Consider:
- Wagering requirements (lower is better)
- Maximum bet limits while using bonus
- Game restrictions and contribution percentages
- Time limits to meet requirements
- Maximum withdrawal limits from bonus winnings

The Real Value Calculation

A £200 bonus with 40x wagering = £8,000 in required bets
A £100 bonus with 25x wagering = £2,500 in required bets

The second offer is actually better value despite being half the amount!

Strategy for Match Bonuses

1. Deposit the maximum amount to get the full bonus
2. If you can't afford the max, deposit an amount that gives you good value
3. Check if the bonus applies to your first deposit only or multiple deposits
4. Understand if wagering applies to bonus only or bonus + deposit

Free Spins Strategy

1. Check which games the spins are valid on
2. Look for free spins with no wagering requirements (rare but valuable)
3. Understand the maximum win limits
4. Use spins on games with higher RTP when possible
5. Don't feel rushed - use spins within the time limit

No Deposit Bonuses

These are excellent for trying a casino risk-free, but:
- Usually have very high wagering requirements
- Often have low maximum withdrawal limits
- May require a deposit before you can withdraw
- Check if you need to make a deposit to activate

Package Bonuses

Multi-deposit packages can offer excellent value:
- First deposit: 100% up to £100
- Second deposit: 50% up to £100
- Third deposit: 25% up to £100

Total potential bonus: £175 across three deposits

Timing Your Deposits

1. Wait for promotional periods when bonuses are enhanced
2. Check if the casino offers reload bonuses regularly
3. Consider if you'll have time to meet wagering requirements
4. Don't claim bonuses you can't realistically use

Game Selection

When using bonus funds:
- Focus on games that contribute 100% to wagering (usually slots)
- Avoid games with low contribution percentages
- Consider RTP - higher is generally better
- Avoid high-variance games that can deplete balance quickly

Bankroll Management

1. Never deposit more than you can afford to lose
2. Set a budget for meeting wagering requirements
3. Don't chase losses trying to clear bonus terms
4. Consider the bonus as part of your entertainment budget

Reading Terms Carefully

Always read the full terms before claiming:
- Wagering requirements and what they apply to
- Game restrictions and contribution percentages
- Maximum bet limits
- Time limits
- Withdrawal restrictions
- Country restrictions

Common Pitfalls to Avoid

1. Claiming bonuses without reading terms
2. Trying to clear requirements too quickly
3. Using bonus on restricted games
4. Exceeding maximum bet limits
5. Not meeting time requirements
6. Expecting to withdraw immediately

Maximizing Long-Term Value

Look beyond the welcome bonus:
- Check for ongoing promotions and reload bonuses
- Consider VIP programs and loyalty rewards
- Look for cashback offers
- Check for regular tournaments and competitions

Remember: The best bonus is one you can actually use and enjoy. Don't be swayed by large numbers if the terms make it impossible to benefit. Focus on bonuses with fair terms that match your playing style and budget.`
                },
              ][selectedGuide].desc}
              icon={[
                {
                  title: 'Understanding Wagering Requirements',
                  desc: 'Learn how to clear bonus terms and withdraw your winnings faster',
                  icon: FaTrophy,
                  fullContent: `Wagering requirements, also known as playthrough requirements, are one of the most important factors to consider when claiming a casino bonus. These requirements determine how many times you must bet your bonus amount before you can withdraw any winnings.

Understanding the Basics

When you claim a welcome bonus, the casino typically requires you to wager the bonus amount (and sometimes the deposit amount) a certain number of times before you can cash out. For example, if you receive a £100 bonus with 35x wagering requirements, you must place bets totaling £3,500 before you can withdraw.

Why Wagering Requirements Exist

Casinos implement wagering requirements to prevent bonus abuse and ensure players actually use the bonus for gaming rather than immediately withdrawing it. This is a standard practice across the industry and is required by UK gambling regulations.

How to Calculate Real Value

To understand the true value of a bonus, calculate the total wagering requirement. A £100 bonus with 35x wagering means £3,500 in bets. Compare this across different casinos to find the best value. Lower wagering requirements (25x-30x) are generally better than higher ones (40x-50x).

Game Contributions

Not all games contribute equally to wagering requirements. Slots typically contribute 100%, while table games like blackjack and roulette often contribute only 10-20%. Live dealer games may contribute even less. Always check the casino's terms to see which games count toward wagering.

Tips for Clearing Requirements

1. Focus on slots if you want to clear requirements faster
2. Avoid high-variance games that can deplete your balance quickly
3. Set a budget and stick to it
4. Take advantage of games with higher RTP (Return to Player) percentages
5. Don't chase losses trying to meet wagering requirements

Time Limits

Most bonuses have time limits, typically 30 days. Make sure you can realistically meet the wagering requirements within this timeframe. If not, the bonus and any winnings may be forfeited.

Reading the Fine Print

Always read the full terms and conditions before claiming any bonus. Look for:
- Wagering requirements (bonus only vs. bonus + deposit)
- Game contribution percentages
- Maximum bet limits while using bonus funds
- Time limits
- Restricted games or payment methods

By understanding wagering requirements, you can make informed decisions about which bonuses offer genuine value and which are simply marketing gimmicks.`
                },
                {
                  title: 'Best Payment Methods for UK Players',
                  desc: 'Compare withdrawal speeds, fees, and security of different payment options',
                  icon: FaClock,
                  fullContent: `Choosing the right payment method is crucial for a smooth casino experience. Different methods offer varying speeds, security levels, and convenience. Here's a comprehensive guide to the best payment options for UK casino players.

E-Wallets: Speed and Convenience

PayPal, Skrill, and Neteller are among the fastest payment methods available. Deposits are typically instant, and withdrawals often process within 24 hours, sometimes even faster. These services act as intermediaries between your bank and the casino, adding an extra layer of security.

Advantages:
- Fast deposits and withdrawals
- Enhanced security and privacy
- Widely accepted at UK casinos
- Easy to manage multiple casino accounts

Considerations:
- Some casinos may charge fees for e-wallet withdrawals
- Not all casinos accept all e-wallet providers
- Account verification may be required

Debit Cards: Traditional and Reliable

Visa and Mastercard debit cards are the most widely accepted payment methods. They're familiar, secure, and convenient for most players.

Advantages:
- Universal acceptance
- Familiar and easy to use
- Strong consumer protection
- Direct bank integration

Considerations:
- Withdrawals can take 3-5 business days
- Some banks may block gambling transactions
- Less privacy than e-wallets

Bank Transfers: For Larger Amounts

Direct bank transfers are ideal for larger deposits and withdrawals. While slower, they're highly secure and often have higher limits.

Advantages:
- High transaction limits
- Very secure
- No intermediary fees
- Suitable for large amounts

Considerations:
- Slower processing (3-5 business days)
- May require additional verification
- Less convenient for smaller transactions

Cryptocurrency: The Future of Payments

Bitcoin and other cryptocurrencies are becoming increasingly popular at UK casinos. They offer anonymity and often instant processing.

Advantages:
- Fast transactions
- Enhanced privacy
- Lower fees
- No chargebacks

Considerations:
- Price volatility
- Not all casinos accept crypto
- Requires understanding of cryptocurrency
- Regulatory considerations

Prepaid Cards: Budget Control

Prepaid cards like Paysafecard allow you to deposit without linking a bank account or card, providing excellent budget control.

Advantages:
- No bank account needed
- Excellent for budget management
- Widely available
- Enhanced privacy

Considerations:
- Cannot be used for withdrawals
- May have lower limits
- Need to purchase separately

Choosing the Right Method

Consider your priorities:
- Speed: Choose e-wallets or cryptocurrency
- Security: Bank transfers or debit cards
- Privacy: E-wallets or cryptocurrency
- Convenience: Debit cards or e-wallets
- Budget control: Prepaid cards

Always check each casino's payment page for specific information about processing times, fees, and limits. Some casinos offer faster processing for VIP members or specific payment methods.`
                },
                {
                  title: 'Maximizing Welcome Bonuses',
                  desc: 'Strategies to get the most value from casino sign-up offers',
                  icon: FaGift,
                  fullContent: `Welcome bonuses can significantly boost your initial bankroll, but maximizing their value requires strategy and understanding. Here's how to get the most from casino sign-up offers.

Understanding Bonus Types

Match Bonuses: The casino matches your deposit up to a certain amount. For example, a 100% match up to £100 means depositing £100 gives you £200 total.

Free Spins: A set number of spins on selected slot games, often with winnings subject to wagering requirements.

No Deposit Bonuses: Free bonus funds or spins just for signing up, requiring no deposit.

Package Bonuses: Multiple bonuses across your first few deposits, offering more total value but requiring multiple deposits.

Evaluating Bonus Value

Don't just look at the bonus amount. Consider:
- Wagering requirements (lower is better)
- Maximum bet limits while using bonus
- Game restrictions and contribution percentages
- Time limits to meet requirements
- Maximum withdrawal limits from bonus winnings

The Real Value Calculation

A £200 bonus with 40x wagering = £8,000 in required bets
A £100 bonus with 25x wagering = £2,500 in required bets

The second offer is actually better value despite being half the amount!

Strategy for Match Bonuses

1. Deposit the maximum amount to get the full bonus
2. If you can't afford the max, deposit an amount that gives you good value
3. Check if the bonus applies to your first deposit only or multiple deposits
4. Understand if wagering applies to bonus only or bonus + deposit

Free Spins Strategy

1. Check which games the spins are valid on
2. Look for free spins with no wagering requirements (rare but valuable)
3. Understand the maximum win limits
4. Use spins on games with higher RTP when possible
5. Don't feel rushed - use spins within the time limit

No Deposit Bonuses

These are excellent for trying a casino risk-free, but:
- Usually have very high wagering requirements
- Often have low maximum withdrawal limits
- May require a deposit before you can withdraw
- Check if you need to make a deposit to activate

Package Bonuses

Multi-deposit packages can offer excellent value:
- First deposit: 100% up to £100
- Second deposit: 50% up to £100
- Third deposit: 25% up to £100

Total potential bonus: £175 across three deposits

Timing Your Deposits

1. Wait for promotional periods when bonuses are enhanced
2. Check if the casino offers reload bonuses regularly
3. Consider if you'll have time to meet wagering requirements
4. Don't claim bonuses you can't realistically use

Game Selection

When using bonus funds:
- Focus on games that contribute 100% to wagering (usually slots)
- Avoid games with low contribution percentages
- Consider RTP - higher is generally better
- Avoid high-variance games that can deplete balance quickly

Bankroll Management

1. Never deposit more than you can afford to lose
2. Set a budget for meeting wagering requirements
3. Don't chase losses trying to clear bonus terms
4. Consider the bonus as part of your entertainment budget

Reading Terms Carefully

Always read the full terms before claiming:
- Wagering requirements and what they apply to
- Game restrictions and contribution percentages
- Maximum bet limits
- Time limits
- Withdrawal restrictions
- Country restrictions

Common Pitfalls to Avoid

1. Claiming bonuses without reading terms
2. Trying to clear requirements too quickly
3. Using bonus on restricted games
4. Exceeding maximum bet limits
5. Not meeting time requirements
6. Expecting to withdraw immediately

Maximizing Long-Term Value

Look beyond the welcome bonus:
- Check for ongoing promotions and reload bonuses
- Consider VIP programs and loyalty rewards
- Look for cashback offers
- Check for regular tournaments and competitions

Remember: The best bonus is one you can actually use and enjoy. Don't be swayed by large numbers if the terms make it impossible to benefit. Focus on bonuses with fair terms that match your playing style and budget.`
                },
              ][selectedGuide].icon}
              fullContent={[
                {
                  title: 'Understanding Wagering Requirements',
                  desc: 'Learn how to clear bonus terms and withdraw your winnings faster',
                  icon: FaTrophy,
                  fullContent: `Wagering requirements, also known as playthrough requirements, are one of the most important factors to consider when claiming a casino bonus. These requirements determine how many times you must bet your bonus amount before you can withdraw any winnings.

Understanding the Basics

When you claim a welcome bonus, the casino typically requires you to wager the bonus amount (and sometimes the deposit amount) a certain number of times before you can cash out. For example, if you receive a £100 bonus with 35x wagering requirements, you must place bets totaling £3,500 before you can withdraw.

Why Wagering Requirements Exist

Casinos implement wagering requirements to prevent bonus abuse and ensure players actually use the bonus for gaming rather than immediately withdrawing it. This is a standard practice across the industry and is required by UK gambling regulations.

How to Calculate Real Value

To understand the true value of a bonus, calculate the total wagering requirement. A £100 bonus with 35x wagering means £3,500 in bets. Compare this across different casinos to find the best value. Lower wagering requirements (25x-30x) are generally better than higher ones (40x-50x).

Game Contributions

Not all games contribute equally to wagering requirements. Slots typically contribute 100%, while table games like blackjack and roulette often contribute only 10-20%. Live dealer games may contribute even less. Always check the casino's terms to see which games count toward wagering.

Tips for Clearing Requirements

1. Focus on slots if you want to clear requirements faster
2. Avoid high-variance games that can deplete your balance quickly
3. Set a budget and stick to it
4. Take advantage of games with higher RTP (Return to Player) percentages
5. Don't chase losses trying to meet wagering requirements

Time Limits

Most bonuses have time limits, typically 30 days. Make sure you can realistically meet the wagering requirements within this timeframe. If not, the bonus and any winnings may be forfeited.

Reading the Fine Print

Always read the full terms and conditions before claiming any bonus. Look for:
- Wagering requirements (bonus only vs. bonus + deposit)
- Game contribution percentages
- Maximum bet limits while using bonus funds
- Time limits
- Restricted games or payment methods

By understanding wagering requirements, you can make informed decisions about which bonuses offer genuine value and which are simply marketing gimmicks.`
                },
                {
                  title: 'Best Payment Methods for UK Players',
                  desc: 'Compare withdrawal speeds, fees, and security of different payment options',
                  icon: FaClock,
                  fullContent: `Choosing the right payment method is crucial for a smooth casino experience. Different methods offer varying speeds, security levels, and convenience. Here's a comprehensive guide to the best payment options for UK casino players.

E-Wallets: Speed and Convenience

PayPal, Skrill, and Neteller are among the fastest payment methods available. Deposits are typically instant, and withdrawals often process within 24 hours, sometimes even faster. These services act as intermediaries between your bank and the casino, adding an extra layer of security.

Advantages:
- Fast deposits and withdrawals
- Enhanced security and privacy
- Widely accepted at UK casinos
- Easy to manage multiple casino accounts

Considerations:
- Some casinos may charge fees for e-wallet withdrawals
- Not all casinos accept all e-wallet providers
- Account verification may be required

Debit Cards: Traditional and Reliable

Visa and Mastercard debit cards are the most widely accepted payment methods. They're familiar, secure, and convenient for most players.

Advantages:
- Universal acceptance
- Familiar and easy to use
- Strong consumer protection
- Direct bank integration

Considerations:
- Withdrawals can take 3-5 business days
- Some banks may block gambling transactions
- Less privacy than e-wallets

Bank Transfers: For Larger Amounts

Direct bank transfers are ideal for larger deposits and withdrawals. While slower, they're highly secure and often have higher limits.

Advantages:
- High transaction limits
- Very secure
- No intermediary fees
- Suitable for large amounts

Considerations:
- Slower processing (3-5 business days)
- May require additional verification
- Less convenient for smaller transactions

Cryptocurrency: The Future of Payments

Bitcoin and other cryptocurrencies are becoming increasingly popular at UK casinos. They offer anonymity and often instant processing.

Advantages:
- Fast transactions
- Enhanced privacy
- Lower fees
- No chargebacks

Considerations:
- Price volatility
- Not all casinos accept crypto
- Requires understanding of cryptocurrency
- Regulatory considerations

Prepaid Cards: Budget Control

Prepaid cards like Paysafecard allow you to deposit without linking a bank account or card, providing excellent budget control.

Advantages:
- No bank account needed
- Excellent for budget management
- Widely available
- Enhanced privacy

Considerations:
- Cannot be used for withdrawals
- May have lower limits
- Need to purchase separately

Choosing the Right Method

Consider your priorities:
- Speed: Choose e-wallets or cryptocurrency
- Security: Bank transfers or debit cards
- Privacy: E-wallets or cryptocurrency
- Convenience: Debit cards or e-wallets
- Budget control: Prepaid cards

Always check each casino's payment page for specific information about processing times, fees, and limits. Some casinos offer faster processing for VIP members or specific payment methods.`
                },
                {
                  title: 'Maximizing Welcome Bonuses',
                  desc: 'Strategies to get the most value from casino sign-up offers',
                  icon: FaGift,
                  fullContent: `Welcome bonuses can significantly boost your initial bankroll, but maximizing their value requires strategy and understanding. Here's how to get the most from casino sign-up offers.

Understanding Bonus Types

Match Bonuses: The casino matches your deposit up to a certain amount. For example, a 100% match up to £100 means depositing £100 gives you £200 total.

Free Spins: A set number of spins on selected slot games, often with winnings subject to wagering requirements.

No Deposit Bonuses: Free bonus funds or spins just for signing up, requiring no deposit.

Package Bonuses: Multiple bonuses across your first few deposits, offering more total value but requiring multiple deposits.

Evaluating Bonus Value

Don't just look at the bonus amount. Consider:
- Wagering requirements (lower is better)
- Maximum bet limits while using bonus
- Game restrictions and contribution percentages
- Time limits to meet requirements
- Maximum withdrawal limits from bonus winnings

The Real Value Calculation

A £200 bonus with 40x wagering = £8,000 in required bets
A £100 bonus with 25x wagering = £2,500 in required bets

The second offer is actually better value despite being half the amount!

Strategy for Match Bonuses

1. Deposit the maximum amount to get the full bonus
2. If you can't afford the max, deposit an amount that gives you good value
3. Check if the bonus applies to your first deposit only or multiple deposits
4. Understand if wagering applies to bonus only or bonus + deposit

Free Spins Strategy

1. Check which games the spins are valid on
2. Look for free spins with no wagering requirements (rare but valuable)
3. Understand the maximum win limits
4. Use spins on games with higher RTP when possible
5. Don't feel rushed - use spins within the time limit

No Deposit Bonuses

These are excellent for trying a casino risk-free, but:
- Usually have very high wagering requirements
- Often have low maximum withdrawal limits
- May require a deposit before you can withdraw
- Check if you need to make a deposit to activate

Package Bonuses

Multi-deposit packages can offer excellent value:
- First deposit: 100% up to £100
- Second deposit: 50% up to £100
- Third deposit: 25% up to £100

Total potential bonus: £175 across three deposits

Timing Your Deposits

1. Wait for promotional periods when bonuses are enhanced
2. Check if the casino offers reload bonuses regularly
3. Consider if you'll have time to meet wagering requirements
4. Don't claim bonuses you can't realistically use

Game Selection

When using bonus funds:
- Focus on games that contribute 100% to wagering (usually slots)
- Avoid games with low contribution percentages
- Consider RTP - higher is generally better
- Avoid high-variance games that can deplete balance quickly

Bankroll Management

1. Never deposit more than you can afford to lose
2. Set a budget for meeting wagering requirements
3. Don't chase losses trying to clear bonus terms
4. Consider the bonus as part of your entertainment budget

Reading Terms Carefully

Always read the full terms before claiming:
- Wagering requirements and what they apply to
- Game restrictions and contribution percentages
- Maximum bet limits
- Time limits
- Withdrawal restrictions
- Country restrictions

Common Pitfalls to Avoid

1. Claiming bonuses without reading terms
2. Trying to clear requirements too quickly
3. Using bonus on restricted games
4. Exceeding maximum bet limits
5. Not meeting time requirements
6. Expecting to withdraw immediately

Maximizing Long-Term Value

Look beyond the welcome bonus:
- Check for ongoing promotions and reload bonuses
- Consider VIP programs and loyalty rewards
- Look for cashback offers
- Check for regular tournaments and competitions

Remember: The best bonus is one you can actually use and enjoy. Don't be swayed by large numbers if the terms make it impossible to benefit. Focus on bonuses with fair terms that match your playing style and budget.`
                },
              ][selectedGuide].fullContent}
            />
          )}
        </div>
      </section>

      {/* Performance Stats */}
      <section className="py-20 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900">
        <div className="container mx-auto px-4">
          <div className="glass rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              <span className="text-gradient">Platform Performance</span>
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Line type="monotone" dataKey="withdrawals" stroke="#0ea5e9" strokeWidth={3} name="Withdrawal Speed %" />
                <Line type="monotone" dataKey="deposits" stroke="#3b82f6" strokeWidth={3} name="Deposit Success %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">By The Numbers</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Trusted by thousands of UK players who rely on our expert reviews
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { number: '50K+', label: 'Active Players', icon: FaUsers },
              { number: '200+', label: 'Casinos Reviewed', icon: FaShieldAlt },
              { number: '98%', label: 'Satisfaction Rate', icon: FaStar },
              { number: '24/7', label: 'Support Available', icon: FaClock },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="glass rounded-xl p-6 text-center card-hover">
                  <div className="w-16 h-16 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-dark-900" />
                  </div>
                  <div className="text-4xl font-bold text-quantum-400 mb-2">{stat.number}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Top Games Section */}
      <section className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Popular Game Categories</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Discover the most popular games across UK casinos
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: 'Slots', count: '15,000+', desc: 'From classic fruit machines to modern video slots', icon: FaGamepad },
              { name: 'Live Casino', count: '500+', desc: 'Real dealers, real-time action from top studios', icon: FaPlay },
              { name: 'Table Games', count: '2,000+', desc: 'Blackjack, roulette, baccarat and more', icon: FaTrophy },
            ].map((game, i) => {
              const Icon = game.icon;
              return (
                <div key={i} className="glass rounded-xl p-8 card-hover">
                  <div className="w-16 h-16 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-full flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-dark-900" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{game.name}</h3>
                  <div className="text-quantum-400 font-semibold text-lg mb-3">{game.count} Games</div>
                  <p className="text-gray-400">{game.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Player Reviews Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">What Players Say</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Real reviews from real players who found their perfect casino
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah M.', rating: 5, text: 'Found the perfect casino thanks to your detailed reviews. The bonus terms were exactly as described!', casino: 'All British Casino' },
              { name: 'James K.', rating: 5, text: 'Withdrawal was super fast, just like you said. Great recommendations!', casino: 'Casino Casino' },
              { name: 'Emma L.', rating: 5, text: 'Love how you break down wagering requirements. Made choosing so much easier.', casino: 'Pub Casino' },
            ].map((review, i) => (
              <div key={i} className="glass rounded-xl p-6">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(review.rating)].map((_, j) => (
                    <FaStar key={j} className="w-5 h-5 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">{review.name}</div>
                    <div className="text-gray-400 text-sm">{review.casino}</div>
                  </div>
                  <FaCommentDots className="w-8 h-8 text-quantum-400 opacity-30" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">How It Works</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Three simple steps to find your perfect casino
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: '01', title: 'Browse Reviews', desc: 'Explore our comprehensive casino reviews with detailed ratings and analysis', icon: FaChartLine },
              { step: '02', title: 'Compare Offers', desc: 'Use our comparison tool to find the best bonuses and terms for you', icon: FaBalanceScale },
              { step: '03', title: 'Start Playing', desc: 'Claim your welcome bonus and enjoy safe, responsible gambling', icon: FaPlay },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="glass rounded-xl p-8 text-center card-hover">
                  <div className="w-20 h-20 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                    <span className="text-3xl font-bold text-dark-900">{item.step}</span>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-quantum-500 rounded-full flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Why Choose JackpotQuantum</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              What makes us the trusted choice for UK casino players
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Expert Reviews', desc: 'Our team tests every casino personally', icon: FaAward },
              { title: 'Unbiased Ratings', desc: 'Honest reviews, no marketing fluff', icon: FaCheckCircle },
              { title: 'Latest Offers', desc: 'Up-to-date bonuses and promotions', icon: FaGift },
              { title: 'Player Safety', desc: 'Only UKGC licensed casinos', icon: FaShieldAlt },
            ].map((feature, i) => {
              const Icon = feature.icon;
              return (
                <div key={i} className="glass rounded-xl p-6 card-hover">
                  <div className="w-12 h-12 bg-gradient-to-br from-quantum-400 to-quantum-600 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-dark-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-20 bg-dark-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Latest Updates</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Stay informed with the latest casino news and updates
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {newsItems.map((news, i) => (
              <div 
                key={i} 
                className="glass rounded-xl p-6 card-hover cursor-pointer"
                onClick={() => setSelectedNews(i)}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1 bg-quantum-500/20 text-quantum-400 rounded-full text-sm font-semibold">{news.category}</span>
                  <span className="text-gray-500 text-sm">{news.date}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{news.title}</h3>
                <p className="text-gray-400 mb-4">{news.desc}</p>
                <button 
                  className="text-quantum-400 hover:text-quantum-300 font-semibold flex items-center space-x-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedNews(i);
                  }}
                >
                  <span>Read More</span>
                  <FaArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {selectedNews !== null && (
            <NewsModal
              isOpen={selectedNews !== null}
              onClose={() => setSelectedNews(null)}
              title={newsItems[selectedNews].title}
              date={newsItems[selectedNews].date}
              category={newsItems[selectedNews].category}
              description={newsItems[selectedNews].desc}
              fullContent={newsItems[selectedNews].fullContent}
            />
          )}
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-20 bg-dark-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Trusted & Licensed</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              We only recommend casinos licensed by the UK Gambling Commission
            </p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8">
            <a 
              href="https://www.gamblingcommission.gov.uk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="glass rounded-xl p-6 hover:bg-dark-700 transition-colors"
            >
              <img 
                src={`${process.env.PUBLIC_URL}/images/footer/gambling-commission.webp`} 
                alt="UK Gambling Commission" 
                className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity" 
              />
            </a>
            <a 
              href="https://www.gambleaware.org" 
              target="_blank" 
              rel="noopener noreferrer"
              className="glass rounded-xl p-6 hover:bg-dark-700 transition-colors"
            >
              <img 
                src={`${process.env.PUBLIC_URL}/images/footer/begambleaware.webp`} 
                alt="BeGambleAware" 
                className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity" 
              />
            </a>
            <a 
              href="https://www.gamcare.org.uk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="glass rounded-xl p-6 hover:bg-dark-700 transition-colors"
            >
              <img 
                src={`${process.env.PUBLIC_URL}/images/footer/gamcare.webp`} 
                alt="GamCare" 
                className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity" 
              />
            </a>
            <a 
              href="https://gamblingtherapy.org" 
              target="_blank" 
              rel="noopener noreferrer"
              className="glass rounded-xl p-6 hover:bg-dark-700 transition-colors"
            >
              <img 
                src={`${process.env.PUBLIC_URL}/images/footer/gambling-therapy-logo.webp`} 
                alt="Gambling Therapy" 
                className="h-16 w-auto opacity-70 hover:opacity-100 transition-opacity" 
              />
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-quantum-900/30 via-dark-900 to-dark-800">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Find Your Perfect Casino?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of UK players who trust JackpotQuantum for honest reviews and the best casino offers
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/comparison"
                className="bg-gradient-to-r from-quantum-500 to-quantum-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-quantum-600 hover:to-quantum-700 transition-all transform hover:scale-105 shadow-lg shadow-quantum-500/30 flex items-center space-x-2"
              >
                <FaBalanceScale className="w-5 h-5" />
                <span>Compare Casinos</span>
              </Link>
              <Link
                to="/about"
                className="glass text-white py-4 px-8 rounded-xl font-bold text-lg hover:bg-dark-700 transition-all flex items-center space-x-2"
              >
                <FaInfoCircle className="w-5 h-5" />
                <span>Learn More</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;

